import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/autoplay'
import {
  selectSkillName,
  selectTestimonials,
  selectTestimonialsLoading,
  selectTestimonialsError,
  fetchTestimonials
} from '../../../../redux/skillSlice'
import {
  imgImage070426At337PmRemovebgPreview1,
  imgImage070426At337PmRemovebgPreview2,
  imgImage070426At337PmRemovebgPreview3,
  imgGroup,
  imgEllipse1334
} from './assets'

const LearnersSection = () => {
  const dispatch = useDispatch()
  const swiperRef = useRef(null)
  
  // Get skill name from store
  const skillName = useSelector(selectSkillName)
  
  // Get testimonials data from store
  const testimonials = useSelector(selectTestimonials)
  const testimonialsLoading = useSelector(selectTestimonialsLoading)
  const testimonialsError = useSelector(selectTestimonialsError)

  // Fallback learners data
  const fallbackLearners = [
    {
      name: "Aman Sharma",
      role: "Product Analyst",
      quote: "Metrics used to confuse me a lot. The way it was broken down here made it click.",
      timeline: "Before 6 months",
      user_image_url: imgImage070426At337PmRemovebgPreview1,
      company_image_url: imgImage070426At337PmRemovebgPreview2,
      isHighlighted: false
    },
    {
      name: "Aman Sharma",
      role: "Product Analyst", 
      quote: "Metrics used to confuse me a lot. The way it was broken down here made it click.",
      timeline: "Before 6 months",
      user_image_url: imgImage070426At337PmRemovebgPreview1,
      company_image_url: imgImage070426At337PmRemovebgPreview2,
      isHighlighted: false
    },
    {
      name: "Aman Sharma",
      role: "Product Analyst",
      quote: "Metrics used to confuse me a lot. The way it was broken down here made it click.",
      timeline: "Before 6 months",
      user_image_url: imgImage070426At337PmRemovebgPreview3,
      company_image_url: imgImage070426At337PmRemovebgPreview2,
      isHighlighted: false
    }
  ]

  // Use API data if available, otherwise fall back to default data
  const learners = testimonials && testimonials.length > 0 
    ? testimonials.map((testimonial, index) => ({
        name: testimonial.name,
        role: testimonial.role,
        quote: testimonial.quote,
        timeline: testimonial.timeline,
        user_image_url: testimonial.user_image_url,
        company_image_url: testimonial.company_image_url,
        isHighlighted: index === 0 // Highlight first testimonial
      }))
    : fallbackLearners

  // Fetch testimonials when component mounts or when skillName changes
  useEffect(() => {
    if (skillName && skillName.trim()) {
      dispatch(fetchTestimonials({ subject_name: skillName.toLowerCase() }))
    }
  }, [dispatch, skillName])

  // Equalize card heights after content loads
  useEffect(() => {
    const equalizeHeights = () => {
      const cards = document.querySelectorAll('.testimonial-card')
      if (cards.length === 0) return

      // Reset heights
      cards.forEach(card => {
        card.style.height = 'auto'
      })

      // Find max height
      let maxHeight = 0
      cards.forEach(card => {
        const height = card.offsetHeight
        if (height > maxHeight) {
          maxHeight = height
        }
      })

      // Apply max height to all cards
      cards.forEach(card => {
        card.style.height = `${maxHeight}px`
      })
    }

    // Run after a short delay to ensure content is rendered
    const timer = setTimeout(equalizeHeights, 100)
    return () => clearTimeout(timer)
  }, [testimonials, testimonialsLoading])

  return (
    <div className="w-full content-stretch flex flex-col gap-[16px] items-start justify-center relative  w-full">
      <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
        <div className="flex flex-col font-['Inter:Bold',sans-serif] font-bold justify-center leading-[0] not-italic relative shrink-0 text-[24px] text-white w-[358px]">
          <p className="leading-[1.3]">Learners Like You</p>
        </div>
      </div>
      
      {/* Show loading state */}
      {testimonialsLoading ? (
        <div className="w-full flex items-center justify-center py-8">
          <div className="text-white opacity-70">Loading testimonials...</div>
        </div>
      ) : (
        <Swiper
          modules={[Autoplay]}
          spaceBetween={16}
          slidesPerView={1.3}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop={true}
          speed={1000}
          className="w-full"
          ref={swiperRef}
        >
          {learners.map((learner, index) => (
            <SwiperSlide key={index}>
              <div className={`testimonial-card ${false ? 'bg-gradient-to-b border-[#448c20] border-[0.7px] border-solid from-[rgba(64,106,255,0.3)] to-[rgba(38,64,153,0.3)]' : 'bg-gradient-to-b from-[rgba(64,106,255,0.28)] to-[rgba(0,0,0,0.4)]'} content-stretch flex flex-col gap-[24px] items-start justify-between pb-[24px] pt-[16px] px-[16px] relative rounded-[16px] shrink-0`}>
                <div className="content-stretch flex gap-[16px] items-center justify-center relative shrink-0">
                  <div className="grid-cols-[max-content] grid-rows-[max-content] inline-grid leading-[0] place-items-start relative shrink-0">
                    <div className="col-1 h-[70px] w-[57px] ml-[-2px] mt-[-4.73px] relative rounded-[8px] row-1 overflow-hidden">
                      <img 
                        alt={learner.name} 
                        className="absolute inset-0 max-w-none object-cover pointer-events-none rounded-[8px] size-full" 
                        src={learner.user_image_url} 
                        onError={(e) => {
                          // Fallback to default image if API image fails to load
                          e.target.src = imgImage070426At337PmRemovebgPreview1
                        }}
                      />
                    </div>
                  </div>
                  <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0">
                    <div className="content-stretch flex items-center relative shrink-0">
                      <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-none not-italic relative shrink-0 text-[14px] text-center text-white whitespace-nowrap">
                        {learner.name}
                      </p>
                    </div>
                    <p className="font-['Inter:Regular',sans-serif] font-normal leading-none not-italic relative shrink-0 text-[#83beff] text-[12px] text-center whitespace-nowrap">
                      {learner.role}
                    </p>
                  </div>
                </div>
                
                <div className="content-stretch flex gap-[9px] items-start relative shrink-0">
                  <div className="content-stretch flex flex-col gap-[8px] items-center justify-center relative self-stretch shrink-0">
                    <div className="bg-white content-stretch flex items-center justify-center relative rounded-[40px] shrink-0 size-[16px]">
                      <div className="w-4 h-4 relative shrink-0 ">
                        <img alt="" className="absolute block inset-0 max-w-none size-full" src={learner.company_image_url} />
                      </div>
                    </div>
                    <div className="bg-[rgba(112,174,255,0.35)] flex-[1_0_0] min-h-px min-w-px rounded-[8px] w-[2px]" />
                    <div className="relative shrink-0 size-[8px]">
                      <div className="w-2 h-2 bg-[#70AEFF]/35 rounded-full" />
                    </div>
                  </div>
                  <div className="content-stretch flex flex-col gap-[16px] items-start relative shrink-0">
                    <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
                      <p className="font-['Inter:Italic',sans-serif] font-normal italic leading-[1.4] relative shrink-0 text-[12px] text-[rgba(255,255,255,0.5)] w-[181px]">
                        "{learner.quote}"
                      </p>
                    </div>
                    <p className="font-['Inter:Medium',sans-serif] font-medium leading-none not-italic relative shrink-0 text-[#83beff] text-[10px] text-center whitespace-nowrap">
                      {learner.timeline}
                    </p>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      
      {/* Show error state if API fails */}
      {testimonialsError && !testimonialsLoading && (
        <div className="w-full text-center py-4">
          <p className="text-red-400 text-sm opacity-70">
            Failed to load testimonials. Showing default content.
          </p>
        </div>
      )}
    </div>
  )
}

export default LearnersSection